import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiMap } from '@/services/data-services/Apis/Apimap';
import { useNavigate, useSearchParams } from 'react-router';
import DocumentCard from './ApiResponseCard';
import Cookies from 'js-cookie';
import {
  accessTokenCookieName,
  backendBaseUrl,
  refreshTokenCookieName,
} from '@/lib/constants';
import axios from 'axios';
import { handleLogout } from '@/services/auth';
import { logout, useAppDispatch } from '@/store';
import { Card } from '@/components/ui/card';
import { NoResult } from '@/assets/icons';
import { ArrowLeft } from 'lucide-react';
import DatePickerInput from '@/components/date-input';

const ApiForm = ({
  selectedApi,
  setSelectedApi,
  responseData,
  setResponseData,
  setIsAuth,
  styledObject,
  hideBackButton,
  autoUpload,
}) => {
  const [searchParams] = useSearchParams();
  const [validationSchema, setValidationSchema] = useState(null);
  // const { inputSchema = {}, documentType = '' } = selectedApi || {};
  const [inputSchema, setInputSchema] = useState({});
  const [documentType, setDocumentType] = useState('');
  const tokenParams = searchParams.get('accessToken');
  const refreshTokenParams = searchParams.get('refreshToken');
  const refreshTokenCookies = Cookies.get(refreshTokenCookieName);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const tokenCookies = Cookies.get(accessTokenCookieName);
  const authToken = tokenParams ? tokenParams : tokenCookies;

  useEffect(() => {
    if (selectedApi && selectedApi?.inputSchema?.type === 'object') {
      setInputSchema(selectedApi?.inputSchema);
      setDocumentType(selectedApi?.documentType);
    } else {
      setInputSchema(selectedApi?.inputSchema?.items[0]);
      setDocumentType(selectedApi?.inputSchema?.items[0]?.documentType);
    }
  }, [selectedApi]);
  useEffect(() => {
    if (inputSchema?.properties) {
      const schemaShape = Object.entries(inputSchema.properties).reduce(
        (item, [key, value]) => {
          const excludedFields = {
            MOBILE_TO_UAN: ['mobile_number'],
            BANK_VERIFICATION: ['ifsc_details'],
            BANK_VERIFICATION_PENNYLESS: ['ifsc_details'],
            DRIVING_LICENSE: ['id_number', 'dob'],
            TAN_COMPANY_SEARCH: ['exact_match'],
            ECHALLAN: ['state_only'],
            EMPLOYMENT_COMPOSITE_API: [
              'aadhaar_number',
              'pan_number',
              'uan_number',
            ],
          };

          if (excludedFields[documentType]?.includes(key)) {
            item[key] = z.any();
            return item;
          }

          let fieldSchema = z.string({
            required_error: `${value.description} is required`,
          });

          if (value.type === 'string' && inputSchema?.required?.includes(key)) {
            fieldSchema = fieldSchema.min(
              1,
              `${value.description} is required`,
            );
          }

          item[key] = fieldSchema;
          return item;
        },
        {},
      );

      const newSchema = z.object(schemaShape);
      setValidationSchema(newSchema);
    }
  }, [inputSchema]);

  const form = useForm({
    resolver: validationSchema ? zodResolver(validationSchema) : undefined,
    defaultValues: Object.keys(inputSchema?.properties || {}).reduce(
      (value, key) => {
        value[key] =
          inputSchema?.properties[key]?.type === 'boolean' ? false : '';
        return value;
      },
      {},
    ),
    mode: 'onChange',
  });

  const { reset, handleSubmit, control } = form;

  const refreshAccessToken = async () => {
    try {
      const refToken =
        localStorage.getItem('refreshTokenCCTWidget') || refreshTokenParams;
      const response = await axios.post(
        `${backendBaseUrl}/auth/refresh-token`,
        {
          refreshToken: refToken || refreshTokenCookies,
        },
      );

      const {
        accessToken,
        ExpiresIn,
        refreshToken: newRefreshToken,
      } = response.data;
      localStorage.setItem('accessTokenCCTWidget', accessToken);
      localStorage.setItem('refreshTokenCCTWidget', newRefreshToken);
      Cookies.set(accessTokenCookieName, accessToken, { expires: ExpiresIn });

      return accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (refreshTokenCookies) {
        dispatch(logout());
        navigate('/');
      }
      setIsAuth(false);
      return error;
    }
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ documentType, body }) => {
      return apiMap[documentType](body, authToken, refreshTokenParams);
    },
    onSuccess: (response) => {
      toast.success('Form submitted successfully!');
      onReset();
      setResponseData(response || {});
      window.parent.postMessage({ type: 'API_RESPONSE', data: response }, '*');
    },
    onError: async (error) => {
      onReset();
      const errorMessage =
        error?.response?.data.messageCode ||
        error?.response?.data?.remark ||
        error?.response?.data.message;
      setResponseData({ error: { message: errorMessage } });

      if (error?.response?.status !== 403) return;

      const originalRequest = error.config;
      originalRequest.retries = (originalRequest.retries || 0) + 1;

      if (originalRequest.retries > 3) {
        handleLogout();
        toast.error(error?.response?.data?.message);
        return;
      }

      try {
        const newAccessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        const retryResponse = await axios(originalRequest);

        setResponseData(retryResponse?.data || {});

        return retryResponse;
      } catch (refreshError) {
        const obj = JSON.parse(JSON.stringify(refreshError));
        window.parent.postMessage({ type: 'API_ERROR', data: obj }, '*');
        setIsAuth(false);
        toast.error('Failed to submit the form');
      }
    },
  });

  const onReset = () => {
    reset(
      Object.keys(inputSchema?.properties || {}).reduce((value, key) => {
        value[key] =
          inputSchema?.properties[key]?.type === 'boolean' ? false : '';
        return value;
      }, {}),
    );
  };

  const formatKey = (key) => {
    const keyMappings = {
      dob: 'Date of Birth',
      id_number: 'ID Number',
    };

    return (
      keyMappings[key] ||
      key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
    );
  };

  const onSubmit = async (formData) => {
    let apiId;
    const apiIdMap = {
      pan_number: formData?.pan_number || null,
      aadhaar_number: formData?.aadhaar_number || null,
      uan_number: formData?.uan_number || null,
    };

    // Then use it like this:
    // apiId = apiIdMap[documentType] || selectedApi?.id;
    apiId = selectedApi?.id;

    const requestBody = {
      apiId: apiId ? apiId : selectedApi?.id,
      documentData:
        documentType === 'EMPLOYMENT_COMPOSITE_API' ? apiIdMap : formData,
    };
    await mutateAsync({
      documentType,
      body: requestBody,
    });
  };

  return (
    <>
      {selectedApi ? (
        <div>
          {!hideBackButton && (
            <Button
              variant="ghost"
              className="mb-6 hover:bg-transparent hover:text-primary px-0"
              onClick={() => {
                setSelectedApi('');
              }}
            >
              <ArrowLeft className="h-5 w-5" /> Back
            </Button>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="w-full">
              <h2 className="text-lg font-normal mb-2">{selectedApi?.name}</h2>
              <div className="text-sm text-placeholder mb-4">
                {selectedApi?.description}
              </div>
              {validationSchema ? (
                <Form {...form}>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {Object.entries(inputSchema?.properties || {}).map(
                        ([key, value], index) => (
                          <>
                            <FormField
                              key={index}
                              control={control}
                              name={key}
                              render={({ field }) => (
                                <FormItem
                                  className={`w-full ${
                                    documentType ===
                                      'EMPLOYMENT_COMPOSITE_API' &&
                                    key === 'uan_number'
                                      ? 'col-span-2'
                                      : ''
                                  }`}
                                >
                                  <FormLabel>{formatKey(key)}</FormLabel>
                                  <FormControl>
                                    {value?.format === 'date' ? (
                                      <DatePickerInput
                                        selected={
                                          field.value
                                            ? new Date(field.value)
                                            : null
                                        }
                                        {...field}
                                        maxDate={new Date()} // Prevent future dates
                                        onBlur={field.onBlur} // Ensure onBlur is called for form validation
                                      />
                                    ) : value.type === 'boolean' ? (
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    ) : (
                                      <Input type="text" {...field} />
                                    )}
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            {documentType === 'EMPLOYMENT_COMPOSITE_API' &&
                              key === 'uan_number' && (
                                <div className="col-span-2 font-medium text-center">
                                  OR
                                </div>
                              )}
                          </>
                        ),
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row justify-between gap-4 mt-6">
                      <Button
                        variant="secondary"
                        type="button"
                        style={styledObject?.resetButton}
                        className="w-full"
                        onClick={onReset}
                      >
                        Reset
                      </Button>
                      <Button
                        id="Submit-Api-Button"
                        type="submit"
                        className="w-full"
                        loading={isPending}
                        style={styledObject?.submitButton}
                      >
                        Submit
                      </Button>
                    </div>
                  </form>
                </Form>
              ) : (
                <p>Loading validation schema...</p>
              )}
            </div>
            <div className="w-full">
              {responseData ? (
                <DocumentCard
                  data={responseData || {}}
                  documentType={documentType}
                  response={responseData.error ? responseData : null}
                  name={selectedApi?.name}
                  setResponseData={setResponseData}
                  selectedApi={selectedApi}
                  AadharApi={mutateAsync}
                  onPDFReady={(file) => {
                    if (autoUpload && file instanceof File) {
                      autoUpload(file);
                    }
                  }}
                />
              ) : (
                <Card className="flex flex-col font-medium items-center justify-center p-5 h-full">
                  <NoResult width={200} height={200} />
                  <p>Enter input and click on submit to get response.</p>
                </Card>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-muted-foreground text-center p-4">
          Please select an API
        </div>
      )}
    </>
  );
};

export default ApiForm;
